import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  Button
} from '@mui/material';
import { AccessTime, CheckCircle, Cancel, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { serviceProviderService, ServiceProvider } from '../../services/serviceProviderService';
import { systemSettingService } from '../../services/systemSettingService';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ServiceProviderSchedule: React.FC = () => {
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [autoAssign, setAutoAssign] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  useEffect(() => {
    loadProviders();
    loadAutoAssignSetting();
  }, []);

  const getDayName = (date: Date): string => {
    const dayIndex = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    return DAYS_OF_WEEK[dayIndex];
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const goToPreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const goToNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const selectedDay = getDayName(selectedDate);
  const isToday = selectedDate.toDateString() === new Date().toDateString();

  const loadAutoAssignSetting = async () => {
    try {
      const setting = await systemSettingService.getByKey('auto_assign_service_provider');
      if (setting) {
        setAutoAssign(setting.attributes?.value === 'true' || setting.value === 'true');
      }
    } catch (error) {
      console.error('Error loading auto-assign setting:', error);
    }
  };

  const loadProviders = async () => {
    try {
      // Get all providers - fetch in batches if needed
      let allProviders: ServiceProvider[] = [];
      let page = 1;
      const pageSize = 100; // Backend maxLimit is 100
      let hasMore = true;

      while (hasMore) {
        const response = await serviceProviderService.getAll({
          filters: { isActive: { $eq: true } },
          populate: '*',
          pagination: { page, pageSize }
        });
        
        const pageData = response.data || [];
        allProviders = [...allProviders, ...pageData];
        
        // Check if there are more pages
        const total = response.meta?.pagination?.total || 0;
        hasMore = allProviders.length < total && pageData.length === pageSize;
        page++;
      }

      setProviders(allProviders);
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  };

  const handleAutoAssignToggle = async (checked: boolean) => {
    try {
      setLoading(true);
      await systemSettingService.upsert(
        'auto_assign_service_provider',
        checked.toString(),
        'Automatically assign requests to available service providers',
        'service_management'
      );
      setAutoAssign(checked);
    } catch (error) {
      console.error('Error updating auto-assign setting:', error);
    } finally {
      setLoading(false);
    }
  };

  const isProviderAvailable = (provider: any, day: string) => {
    const attrs = provider.attributes || provider;
    if (!attrs.schedule || typeof attrs.schedule !== 'object') return false;
    return attrs.schedule[day]?.active || false;
  };

  const getProviderSchedule = (provider: any, day: string) => {
    const attrs = provider.attributes || provider;
    if (!attrs.schedule || typeof attrs.schedule !== 'object') return null;
    return attrs.schedule[day];
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <IconButton 
            onClick={goToPreviousDay}
            color="primary"
            aria-label="Previous day"
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <ChevronLeft />
          </IconButton>
          <Box sx={{ textAlign: 'center', minWidth: '200px' }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {formatDate(selectedDate)}
            </Typography>
            {!isToday && (
              <Button 
                size="small" 
                onClick={goToToday}
                sx={{ mt: 0.5 }}
              >
                Go to Today
              </Button>
            )}
          </Box>
          <IconButton 
            onClick={goToNextDay}
            color="primary"
            aria-label="Next day"
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={autoAssign}
              onChange={(e) => handleAutoAssignToggle(e.target.checked)}
              disabled={loading}
            />
          }
          label="Auto-Assign Requests"
        />
      </Box>

      {autoAssign && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Auto-assign is enabled. New requests will be automatically assigned to available service providers based on their schedule.
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {isToday ? 'Today' : selectedDay} ({selectedDay}) - {providers.length} Service Providers
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
              xl: 'repeat(5, 1fr)'
            },
            gap: 1.5
          }}
        >
          {providers.map((provider) => {
            const attrs = provider.attributes || provider;
            const available = isProviderAvailable(provider, selectedDay);
            const schedule = getProviderSchedule(provider, selectedDay);
            
            return (
              <Card
                key={provider.id}
                sx={{
                  height: '100%',
                  border: available ? '2px solid #4caf50' : '1px solid #ddd',
                  backgroundColor: available ? '#f1f8f4' : '#fafafa',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {attrs.name}
                    </Typography>
                    {available ? (
                      <CheckCircle color="success" sx={{ fontSize: '1.2rem' }} />
                    ) : (
                      <Cancel color="disabled" sx={{ fontSize: '1.2rem' }} />
                    )}
                  </Box>
                  <Chip
                    label={attrs.type}
                    size="small"
                    sx={{ mb: 0.5, height: '20px', fontSize: '0.7rem' }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.75rem' }}>
                    {attrs.phone}
                  </Typography>
                  {attrs.area && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                      {attrs.area}
                    </Typography>
                  )}
                  {available && schedule && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                      <AccessTime fontSize="small" color="primary" sx={{ fontSize: '0.9rem' }} />
                      <Typography variant="caption" color="primary" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                        {schedule.from || '08:00'} - {schedule.to || '17:00'}
                      </Typography>
                    </Box>
                  )}
                  {!available && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontSize: '0.7rem' }}>
                      Not available
                    </Typography>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
        {providers.filter(p => isProviderAvailable(p, selectedDay)).length === 0 && (
          <Box sx={{ width: '100%', py: 3 }}>
            <Typography variant="body1" color="text.secondary" align="center">
              No service providers available on {formatDate(selectedDate)}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ServiceProviderSchedule;

